<template>
  <div class="lg:flex lg:items-stretch min-h-screen">
    <div class="w-full lg:w-3/4 lg:pr-16">
      <div class="font-heading font-semibold text-4xl mb-3">Performances</div>
      <ReportsTableFilter @search="searchQuery = $event" @filterByDateRange="dateFilter = $event" />
      <DataTable id="report-data-table" :columns="reportColumns" :items="filteredPerformances" />
    </div>
    <div class="w-full lg:w-1/4">
      <div class="font-heading font-semibold text-xl mt-3 mb-3">Reservations</div>
      <ExportForm
        uid="reservations"
        baseUrl="/api/reservation/download/"
        :reportTypes="exportReportType.reservations"
      />
      <div class="font-heading font-semibold text-xl mt-12 mb-3">Transactions</div>
      <ExportForm
        uid="transactions"
        baseUrl="/api/transaction/download_transactions/"
        :reportTypes="exportReportType.transactions"
      />
      <div class="font-heading font-semibold text-xl mt-12 mb-3">Guests</div>
      <ExportForm
        uid="guests"
        baseUrl="/api/guest/download/"
        :reportTypes="exportReportType.guests"
        :startEndDates="false"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import DataTable from '@/common/components/organisms/DataTable.vue'
import ReportsTableFilter from '@/components/ReportsTableFilter.vue'
import ExportForm from '@/components/reports/ExportForm.vue'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import { useSettingStore } from '@/stores/setting'
import { cleanString, sortBy } from '@/utils/utils'

import { PerformanceQueryParams, PerformanceReportRead } from '@generated/types'

interface DisplayReport extends PerformanceReportRead {
  netFace: number
  date: string
  time: string
  percentSold: number
}

export default defineComponent({
  name: 'ReportsOverview',
  components: {
    ReportsTableFilter,
    DataTable,
    ExportForm,
  },
  data() {
    return {
      reports: [] as PerformanceReportRead[],
      searchQuery: '',
      dateFilter: {
        startDate: '',
        endDate: '',
      },
    }
  },
  methods: {
    async fetchReports() {
      try {
        const params = {} as PerformanceQueryParams
        if (this.dateFilter.startDate && this.dateFilter.endDate) {
          params.start_date = this.dateFilter.startDate
          params.end_date = this.dateFilter.endDate
        }
        this.reports = await this.performanceStore.getPerformanceReports(params)
        sortBy(this.reports, '-datetime')
      } catch (error) {
        console.error(error)
      }
    },
  },
  watch: {
    dateFilter: {
      handler: 'fetchReports',
      deep: true,
    },
  },
  computed: {
    ...mapStores(usePerformanceStore, useSettingStore),

    filteredPerformances(): DisplayReport[] {
      return this.reports
        .map(report => ({
          ...report,
          showName: report.show.name,
          date: this.settingStore.dateIsoStr(report.datetime),
          time: this.settingStore.timeStr(report.datetime),
          netFace: +report.net_face,
          percentSold: +((report.booked_tables * 100) / (report.table_count || 1)).toFixed(0),
        }))
        .filter(report =>
          this.searchQuery ? cleanString(report.showName).includes(this.searchQuery) : true,
        )
    },

    reportColumns() {
      return [
        { field: 'showName', label: 'Show' },
        { field: 'date', label: 'Date' },
        { field: 'time', label: 'Time' },
        {
          field: 'netFace',
          label: 'Net Sales',
          classes: 'text-right relative',
          tooltip: 'Tickets - Discounts - Refunds',
          mask: value => this.settingStore.currency(value).split('.')[0],
        },
        { field: 'booked_guests', label: 'Tickets', classes: 'text-right' },
        { field: 'percentSold', label: '% Sold', classes: 'text-right' },
      ]
    },

    exportReportType() {
      return {
        reservations: {
          'reservations-report-by-booking': {
            uid: 'reservations-report-by-booking-input',
            label: 'By Created',
            value: 'byBooking',
          },
          'reservations-report-by-showdate': {
            uid: 'reservations-report-by-showdate-input',
            label: 'By Performance',
            value: 'byShowDate',
          },
        },
        transactions: {
          'transactions-report-by-booking': {
            uid: 'transactions-report-by-booking-input',
            label: 'By Created',
            value: 'byBooking',
          },
          'transactions-report-by-showdate': {
            uid: 'transactions-report-by-showdate-input',
            label: 'By Performance',
            value: 'byShowDate',
          },
        },
        guests: {
          'guest-report-by-subscribed': {
            uid: 'guest-report-by-subscribed-input',
            label: 'Subscribed',
            value: 'bySubscribed',
          },
          'guest-report-by-unsubscribed': {
            uid: 'guest-report-by-unsubscribed-input',
            label: 'Unsubscribed',
            value: 'byUnsubscribed',
          },
        },
      }
    },
  },
})
</script>
